import React, {
    useCallback,
    useMemo,
    useEffect,
    useRef,
    useState,
} from "react";
import Editor from "@monaco-editor/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Check, AlertCircle, Save } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { useWorkflowGeneration } from "../../../../context/WorkflowGenerationContext";
import { useWorkflow } from "../../../../context/WorkflowContext";

export default function CodeTab({ step, isExpanded }) {
    const { getStepConfig, handleSetStepsConfig } = useWorkflowGeneration();
    const { refetchWorkflow } = useWorkflow();
    const { call: saveStep, isLoading: isSaving } = useBluelagoon({
        method: "updateWorkflowStep",
    });
    const { t } = useTranslation();
    const stepConfig = getStepConfig(step.id);
    const code = stepConfig?.code || step.config?.code || "";
    const language =
        step.config?.language === "react"
            ? "javascript"
            : step.config?.language || "javascript";
    const codeRef = useRef(code);
    const lineCount = useMemo(() => code.split("\n").length, [code]);
    const [saveStatus, setSaveStatus] = useState(null);
    const statusTimer = useRef(null);
    useEffect(() => {
        codeRef.current = code;
    }, [code]);

    const handleSave = useCallback(async () => {
        setSaveStatus("saving");
        try {
            const response = await saveStep({
                stepId: step.id,
                config: { ...step.config, code: codeRef.current },
                isDraft: true,
            });
            if (response?.error) {
                setSaveStatus("error");
                toast.error(response.error);
            } else {
                setSaveStatus("saved");
                refetchWorkflow();
            }
        } catch (error) {
            setSaveStatus("error");
            toast.error(t("common.saveFailed") || "Save failed");
        }
        if (statusTimer.current) clearTimeout(statusTimer.current);
        statusTimer.current = setTimeout(() => setSaveStatus(null), 3000);
    }, [step.id, step.config, saveStep, t, refetchWorkflow]);

    const handleCodeChange = useCallback(
        (value) => {
            if (value !== undefined) {
                handleSetStepsConfig(step.id, { code: value });
            }
        },
        [step.id, handleSetStepsConfig]
    );

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "s") {
                e.preventDefault();
                e.stopPropagation();
                handleSave();
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleSave]);

    const editorOptions = useMemo(
        () => ({
            minimap: { enabled: isExpanded },
            fontSize: isExpanded ? 14 : 13,
            lineNumbers: "on",
            scrollBeyondLastLine: false,
            wordWrap: "off",
            folding: true,
            tabSize: 2,
            automaticLayout: true,
        }),
        [isExpanded]
    );

    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 bg-gray-50">
                <span className="text-xs text-gray-500 font-mono">
                    {language}
                </span>
                <div className="flex items-center gap-2">
                    {saveStatus === "saved" && (
                        <span className="flex items-center gap-1 text-[10px] text-green-600">
                            <Check className="h-3 w-3" />
                            {t("common.saved")}
                        </span>
                    )}
                    {saveStatus === "error" && (
                        <span className="flex items-center gap-1 text-[10px] text-red-600">
                            <AlertCircle className="h-3 w-3" />
                            {t("common.saveFailed") || "Save failed"}
                        </span>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-green-600 hover:bg-green-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSaving ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                            <Save className="h-3 w-3" />
                        )}
                        {isSaving ? t("common.saving") : t("common.save")}
                    </button>
                    <span className="text-xs text-gray-400">
                        {lineCount} lines
                    </span>
                </div>
            </div>
            <div className="flex-1 min-h-[300px]">
                <Editor
                    height="100%"
                    language={language}
                    value={code}
                    onChange={handleCodeChange}
                    options={editorOptions}
                    theme="vs"
                />
            </div>
        </div>
    );
}
